import type {
    AllCoverablesDTO,
    CoverableDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import type {
    AllCoverablesOnsaDTO,
    CoverableOnsaDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import {
    sortCoverablesByFlavourName,
    sortCoverablesByFlavourNameFieldsAndId
} from '../../../utils/sortingUtil';
import type { ProductStatus } from '../../../recoil/types/onsaTypes';
import {
    allCoverablesOnsaToCoverableDTOArray,
    allCoverablesToCoverableDTOArray
} from '../../../utils/coverablesUtil';
import type { FeeDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/quote/submission/dto';
import { COVERABLE_CODE } from '../../../constants/coverable';

const getSortedCoverables = (
    coverables: CoverableDTO[]
): { sortedCoverables: CoverableDTO[]; sortedRemovedCoverables: CoverableDTO[] } => {
    const sort = (status: string): CoverableDTO[] => {
        return coverables.filter((obj) => obj.status === status).sort(sortCoverablesByFlavourName);
    };

    const sortedAddedCoverables = sort('Added');
    const sortedChangedCoverables = sort('Changed');
    const sortedRemovedCoverables = sort('Removed');

    return {
        sortedCoverables: sortedAddedCoverables.concat(sortedChangedCoverables),
        sortedRemovedCoverables
    };
};

const getSortedByCoverablePrice = (
    coverables: CoverableDTO[]
): {
    sortedCoverables: CoverableDTO[];
} => {
    const calculatedProducts = coverables
        .filter((obj) => !!obj.monthlyPrice?.amount && obj.monthlyPrice.amount > 0)
        .sort(sortCoverablesByFlavourName);
    const uncalculatedProdcuts = coverables
        .filter((obj) => !obj.monthlyPrice?.amount || obj.monthlyPrice?.amount === 0)
        .sort(sortCoverablesByFlavourNameFieldsAndId);

    return { sortedCoverables: calculatedProducts.concat(uncalculatedProdcuts) };
};

const coverableMap: Record<string, string> = {
    Hundeforsikring: 'dogs',
    Katteforsikring: 'cats',
    Kennelforsikring: 'kennels',
    Ulykkesforsikring: 'adultAccidents',
    Børneforsikring: 'childAccidents',
    'Ulykkesforsikring til unge': 'youngAccidents',
    Rejseforsikring: 'travels',
    'Rejseforsikring til enkeltperson': 'travels',
    Indboforsikring: 'privateContent',
    'Indboforsikring til unge': 'youthContent',
    Bilforsikring: 'cars',
    Fritidshusforsikring: 'holidayHomes',
    Husforsikring: 'houses',
    Campingvognsforsikring: 'caravans',
    Påhængsvognsforsikring: 'trailers'
};

const removeCoverableFromAllCoverablesDTO = (
    allCoverables: AllCoverablesDTO,
    coverable: CoverableDTO
): AllCoverablesDTO | undefined => {
    if (!coverable.flavourName) {
        return undefined;
    }

    const index = coverableMap[coverable.flavourName] as keyof typeof allCoverables;

    if (!(index in allCoverables)) {
        return undefined;
    }

    const productCoverables: CoverableDTO[] = allCoverables[index] ?? [];

    return {
        ...allCoverables,
        [index]: productCoverables?.filter((cov) => cov.fixedId !== coverable.fixedId)
    };
};

const removeCoverableFromAllCoverablesOnsaDTO = (
    allCoverablesOnsaDTO: AllCoverablesOnsaDTO,
    coverable: CoverableDTO
): AllCoverablesOnsaDTO | undefined => {
    if (!coverable.flavourName) {
        return undefined;
    }

    const index = coverableMap[coverable.flavourName] as keyof AllCoverablesOnsaDTO;

    if (!(index in allCoverablesOnsaDTO)) {
        return undefined;
    }

    const productCoverables: CoverableOnsaDTO[] = allCoverablesOnsaDTO[index] ?? [];

    return {
        ...allCoverablesOnsaDTO,
        [index]: productCoverables?.filter((cov) => cov.fixedId !== coverable.fixedId)
    };
};

const removeCoverableFromProductStatusList = (
    productStatusList: Record<string, ProductStatus[]>,
    coverable: CoverableDTO
): Record<string, ProductStatus[]> | undefined => {
    if (!coverable.flavourName) {
        return undefined;
    }

    const updatedList = { ...productStatusList };

    const index = coverableMap[coverable.flavourName] as keyof typeof updatedList;

    if (!(index in productStatusList)) {
        return undefined;
    }

    updatedList[index] = updatedList[index]?.filter(
        (productStatus) => productStatus.fixedId !== coverable.fixedId
    );

    return Object.fromEntries(Object.entries(updatedList).filter(([_, value]) => value.length > 0));
};

const sameCoverableAndProductStatus = (
    coverable: CoverableDTO,
    productStatus: ProductStatus
): boolean => {
    return (
        coverable.lobCode === productStatus.lobCode && coverable.fixedId === productStatus.fixedId
    );
};

const isOnsaCoverable = (
    coverable: CoverableDTO,
    productStatusList: Record<string, ProductStatus[]>
): boolean => {
    if (!coverable.flavourName) {
        return false;
    }

    const index = coverableMap[coverable.flavourName];

    if (index && productStatusList[index]) {
        return productStatusList[index]
            .map((productStatus) => sameCoverableAndProductStatus(coverable, productStatus))
            .some(Boolean);
    }
    return false;
};

const numberOfCoverablesInShoppingBasket = (
    allCoverablesDTO: AllCoverablesDTO,
    allCoverablesOnsaDTO: AllCoverablesOnsaDTO
): number => {
    const allShoppingBasketCoverables = allCoverablesToCoverableDTOArray(allCoverablesDTO);
    const allOnsaCoverables = allCoverablesOnsaToCoverableDTOArray(allCoverablesOnsaDTO);
    return allShoppingBasketCoverables.length + allOnsaCoverables.length;
};

const shouldHideName = (coverable: CoverableDTO, isLoggedOn: boolean): boolean => {
    if (
        'isDummyContact' in coverable &&
        coverable.isDummyContact &&
        'isForOtherPerson' in coverable &&
        !coverable.isForOtherPerson &&
        coverable.flavourCode !== COVERABLE_CODE.childAccident
    ) {
        return true;
    }
    if (coverable.lobCode === 'TRA' && !isLoggedOn) return true;
    return false;
};

const filterGovernmentFees = (governmentFees: FeeDTO[]): FeeDTO[] => {
    return governmentFees.filter(
        (taxes) =>
            taxes.feeName &&
            taxes.amount?.amount &&
            !taxes.feeName?.includes('Skadeforsikringsafgift') &&
            !taxes.chargeCode_Top
    );
};

export {
    getSortedCoverables,
    getSortedByCoverablePrice,
    removeCoverableFromAllCoverablesDTO,
    removeCoverableFromAllCoverablesOnsaDTO,
    removeCoverableFromProductStatusList,
    sameCoverableAndProductStatus,
    isOnsaCoverable,
    numberOfCoverablesInShoppingBasket,
    shouldHideName,
    filterGovernmentFees
};
